/**
 * Pure tap decision + drawer rule for Explore map bank labels.
 * Kept free of Mapbox so unit tests cover apply / clear / no-op / drawer.
 */

import {
  isActiveBankFilterLabel,
  matchBankLabel,
  type BankLabelMatch,
  type BankLabelMatchMachine,
} from './bankLabelMatch';

export type BankLabelTapAction =
  | { type: 'noop' }
  | { type: 'clear' }
  | { type: 'apply'; match: BankLabelMatch };

export type BankLabelTapContext = {
  /** Current SelectedSlotsName (chip text). */
  selectedSlotsName?: string | null;
  /** Redux bankFilter flag. */
  isBankFilter: boolean;
};

/**
 * Decide what a bank-label tap should do (before map/drawer side effects).
 * Non-bank labels and labels with no matching machines → noop.
 */
export function decideBankLabelTap(
  label: string | null | undefined,
  machines: BankLabelMatchMachine[] | null | undefined,
  ctx: BankLabelTapContext
): BankLabelTapAction {
  const match = matchBankLabel(label, machines);
  if (!match) return { type: 'noop' };

  if (isActiveBankFilterLabel(ctx.selectedSlotsName, ctx.isBankFilter, match.displayLabel)) {
    return { type: 'clear' };
  }
  // Also treat normalized key equality (chip may be "Bank 9G" vs label "9G").
  if (isActiveBankFilterLabel(ctx.selectedSlotsName, ctx.isBankFilter, match.key)) {
    return { type: 'clear' };
  }

  return { type: 'apply', match };
}

/**
 * Snap ids that already show ≥50% of the screen as drawer height.
 * Below these → raise to Mid (50%). Mid / ThreeQuarters / taller → leave alone.
 */
const DRAWER_AT_OR_ABOVE_HALF = new Set([
  'mid',
  'half',
  'threeQuarters',
  'max',
  'full',
]);

/**
 * Drawer rule (Miller 2026-10-18): if below 50%, raise to Mid; if already at
 * 50% or 75% (ThreeQuarters) — or higher — leave it.
 */
export function shouldRaiseDrawerForBankTap(currentSnap: string | null | undefined): boolean {
  if (!currentSnap) return true;
  return !DRAWER_AT_OR_ABOVE_HALF.has(currentSnap);
}

/** Snap to force when raising for a bank tap. */
export const BANK_LABEL_DRAWER_SNAP = 'mid' as const;
